import { useDisclosure } from "@mantine/hooks";
import { useCallback, useMemo, useState } from "react";
import ScrollableTable from "src/components/shared/ScrollableTable/ScrollableTable";
import {
  type IComment,
  type ISummaryItem,
} from "src/pages/Summary/Summary.types";
import { deepClone } from "src/utils";

import CheckListsModal from "../../../../generator/_Modals/CheckListsModal/CheckListsModal";
import useNonMaritime from "../../../../hooks/useNonMaritime";
import CommentsModal from "../CommentsModal/CommentsModal";
import { reviewsTableHeaders } from "./ReviewsTable.utils";
import SummaryItemRow from "./SummaryItemRow/SummaryItemRow";

interface Props {
  reviews: ISummaryItem[];
  setReviewComments?: (
    comments: IComment[],
    lbeid: string | undefined,
    partid: string | undefined,
    meid: string | undefined,
    weid: string | undefined,
  ) => void;
  loading: boolean;
  readonlyComments?: boolean;
  isMedicalLog?: boolean;
  select?: boolean;
  red?: boolean;
  style?: React.CSSProperties;
  isPdfExport?: boolean;
}

const ReviewsTable = ({
  reviews,
  readonlyComments,
  setReviewComments,
  loading,
  isMedicalLog,
  select,
  red,
  style,
  isPdfExport,
}: Props) => {
  const [review, setReview] = useState<ISummaryItem | null>(null);
  const [commentsOpened, { open: openComments, close: closeComments }] =
    useDisclosure(false);
  const { replaceMaritimeText } = useNonMaritime();
  const parsedHeaders = useMemo(() => {
    const parsed: any[] = deepClone(reviewsTableHeaders);
    if (isMedicalLog) {
      parsed[2] = {
        ...parsed[2],
        name: "subject",
        label: "Subject",
      };
      parsed.splice(5, 1);
      parsed.splice(
        5,
        0,
        { name: "logStatus", label: "Log Status", style: { width: "15%" } },
        { name: "eventStatus", label: "Event Status", style: { width: "15%" } },
      );
    }
    if (select) {
      parsed.push({ name: "select", label: "Select", style: { width: "5%" } });
    }

    if (isPdfExport) {
      const blankIndex = parsed.findIndex(h => h.name === "blank");
      if (blankIndex !== -1) parsed.splice(blankIndex, 1);
    }

    return parsed.map(h => ({ ...h, label: replaceMaritimeText(h.label) }));
  }, [isMedicalLog, select, replaceMaritimeText, isPdfExport]);

  const handleOpenComments = useCallback(
    (review: ISummaryItem) => {
      setReview(review);
      openComments();
    },
    [openComments],
  );

  const handleSetReviewComments = useCallback(
    (
      comments: IComment[],
      lbeid: string | undefined,
      partid: string | undefined,
      meid: string | undefined,
      weid: string | undefined,
    ) => {
      setReview(prev => prev && { ...prev, comments });
      setReviewComments &&
        setReviewComments(comments, lbeid, partid, meid, weid);
    },
    [setReviewComments],
  );

  const handleCloseModal = () => {
    setReview(null);
    closeComments();
  };

  const renderRow = useCallback(
    ({ row, index }: { row: ISummaryItem; index?: number }) => (
      <SummaryItemRow
        row={row}
        isSelect={select}
        openComments={handleOpenComments}
        isMedicalLog={isMedicalLog}
        rowIndex={index}
        red={red}
        isPdfExport={isPdfExport}
      />
    ),
    [handleOpenComments, isMedicalLog, select, red, isPdfExport],
  );
  return (
    <>
      <ScrollableTable
        data={reviews}
        headers={parsedHeaders}
        renderRow={renderRow}
        loading={loading}
        sortable
        red={red}
        style={style}
        isPdfExport={isPdfExport}
      />
      <CheckListsModal type="summary" />
      <CommentsModal
        opened={commentsOpened}
        onClose={handleCloseModal}
        lbeid={review?.lbeid}
        partid={review?.partid}
        meid={review?.meid}
        weid={review?.weid}
        comments={review?.comments}
        setReviewComments={handleSetReviewComments}
        type={readonlyComments ? "readonly" : "editable"}
      />
    </>
  );
};

export default ReviewsTable;
